import React, { useState, useContext, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import Spinner from "../../components/Spinner";
import DeviceProfilesCardView from "./DeviceProfilesCardView";
import DeviceProfilesListingView from "./DeviceProfilesListingView";
import DeviceProfileSidebar from "./DeviceProfileSidebar";
import AddDeviceProfileWizard from "./AddDeviceProfileWizard";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Device Profiles",
    ENTITY_SINGULAR: "Device Profile",
    ENTITY_PLURAL: "Device Profiles",
    DELETE_TITLE_SINGLE: (name) => `Delete Device Profile ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Device Profiles?`,
    DELETE_MESSAGE_SINGLE: (
        name,
    ) => `You are about to delete device profile "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} device profile${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total device profiles selected: ${count}`,
};

const GET_DEVICE_PROFILES = gql`
    query GetDeviceProfiles($plant_id: Int!) {
        deviceProfiles(where: { plant_id: { equals: $plant_id } }) {
            id
            name
            description
            configuration
            plant_id
            created_at
            updated_at
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
        }
    }
`;

const CREATE_ONE_DEVICE_PROFILE = gql`
    mutation CreateOneDeviceProfile($data: DeviceProfileCreateInput!) {
        createOneDeviceProfile(data: $data) {
            id
            name
            description
            configuration
        }
    }
`;

const UPDATE_ONE_DEVICE_PROFILE = gql`
    mutation UpdateOneDeviceProfile(
        $data: DeviceProfileUpdateInput!
        $where: DeviceProfileWhereUniqueInput!
    ) {
        updateOneDeviceProfile(data: $data, where: $where) {
            id
            name
            description
            configuration
        }
    }
`;

const DELETE_ONE_DEVICE_PROFILE = gql`
    mutation DeleteOneDeviceProfile($id: Int!) {
        deleteOneDeviceProfile(where: { id: $id }) {
            id
        }
    }
`;

const DeviceProfiles = () => {
    const context = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite   = can("device_profile", "WRITE");
    const canDestroy = can("device_profile", "DESTROY");
    const [searchParams, setSearchParams] = useSearchParams();

    const { data, loading, refetch } = useQuery(GET_DEVICE_PROFILES, {
        variables: { plant_id: context?.lastUsedPlant?.id },
        skip: !context?.lastUsedPlant?.id,
        client: context.engineClient,
    });

    const [createDeviceProfile] = useMutation(CREATE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });
    const [updateDeviceProfile] = useMutation(UPDATE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });
    const [deleteOneDeviceProfile] = useMutation(DELETE_ONE_DEVICE_PROFILE, {
        client: context.engineClient,
    });

    const [showSidebar, setShowSidebar] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [viewMode, setViewMode] = useState("card");
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);

    const deviceProfiles = data?.deviceProfiles ?? [];

    const filteredProfiles = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return deviceProfiles;
        return deviceProfiles.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [deviceProfiles, filterText]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleAdd = () => {
        setSelectedRows([]);
        setShowSidebar(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowSidebar(true);
    };

    const handleSidebarClose = async () => {
        setShowSidebar(false);
        setSelectedRows([]);
        setToggleCleared(!toggleCleared);
        await refetch();
    };

    const handleSaveDeviceProfile = async (profilePayload) => {
        try {
            const currentUserId =
                context?.currentUser?.id ||
                context?.user?.id ||
                context?.me?.id ||
                2;

            if (!profilePayload.id) {
                // Create
                await createDeviceProfile({
                    variables: {
                        data: {
                            name: profilePayload.name,
                            description: profilePayload.description || "",
                            configuration: profilePayload.configuration || {},
                            plant: {
                                connect: { id: context.lastUsedPlant.id },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateDeviceProfile({
                    variables: {
                        where: { id: profilePayload.id },
                        data: {
                            name: { set: profilePayload.name },
                            description: {
                                set: profilePayload.description || "",
                            },
                            configuration: profilePayload.configuration,
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            await handleSidebarClose();
            context.showSuccess(
                profilePayload.id
                    ? `Device Profile "${profilePayload.name}" updated successfully`
                    : `Device Profile "${profilePayload.name}" created successfully`,
            );
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    const handleDeleteDeviceProfile = async (profile) => {
        try {
            await deleteOneDeviceProfile({
                variables: { id: profile.id },
            });
            await handleSidebarClose();
            context.showSuccess(
                `Device Profile "${profile.name}" deleted successfully`,
            );
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    const handleDelete = (profiles) => {
        if (!profiles || profiles.length === 0 || isDeleting) return;

        const isSingle = profiles.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(profiles[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(profiles.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(profiles[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(profiles.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(profiles.length)}
                        &nbsp;
                        <span className="badge bg-info">{profiles.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                try {
                    for (const profile of profiles) {
                        try {
                            await deleteOneDeviceProfile({
                                variables: { id: profile.id },
                            });
                        } catch (err) {
                            context.showError(err);
                        } finally {
                            setDeleteDone((d) => d + 1);
                        }
                    }
                    await refetch();
                    setSelectedRows([]);
                    setToggleCleared(!toggleCleared);
                } finally {
                    setIsDeleting(false);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleUpdateConfiguration = async (profileId, configuration) => {
        try {
            await updateDeviceProfile({
                variables: {
                    where: { id: profileId },
                    data: {
                        configuration: configuration,
                    },
                },
            });
            await refetch();
        } catch (error) {
            context.showError(error);
            throw error;
        }
    };

    if (loading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-sd-card fa-10x text-primary"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {deviceProfiles && (
                <>
                    <div className="font-weight-bold mb-3">
                        {UI_STRINGS.PAGE_TITLE}
                    </div>

                    <EntityOperationBar
                        entity={{
                            singular: UI_STRINGS.ENTITY_SINGULAR,
                            plural: UI_STRINGS.ENTITY_PLURAL,
                        }}
                        allowSearch={true}
                        allowAdd={canWrite}
                        allowDelete={canDestroy}
                        allowEdit={canWrite}
                        defaultView={viewMode}
                        selectedRows={selectedRows}
                        isDeleting={isDeleting}
                        deleteDone={deleteDone}
                        onSearch={(text) => setFilterText(text)}
                        onAdd={handleAdd}
                        onViewChange={(mode) => {
                            setViewMode(mode);
                            setSearchParams({ view: mode });
                            setSelectedRows([]);
                        }}
                        onEdit={handleEdit}
                        onDelete={() => handleDelete(selectedRows)}
                    >
                        <EntityOperationBar.View
                            mode="card"
                            icon="fa fa-th"
                            tooltip="Card View"
                        >
                            <DeviceProfilesCardView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                toggleCleared={toggleCleared}
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowSidebar(true);
                                }}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                            />
                        </EntityOperationBar.View>

                        <EntityOperationBar.View
                            mode="list"
                            icon="fa fa-list"
                            tooltip="List View"
                        >
                            <DeviceProfilesListingView
                                profiles={filteredProfiles}
                                selectedRows={selectedRows}
                                onSelectedRowsChange={({ selectedRows }) =>
                                    setSelectedRows(selectedRows)
                                }
                                toggleCleared={toggleCleared}
                                onEdit={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowSidebar(true);
                                }}
                                onConfigure={(profile) => {
                                    setSelectedRows([profile]);
                                    setShowWizard(true);
                                }}
                            />
                        </EntityOperationBar.View>
                    </EntityOperationBar>
                </>
            )}

            <DeviceProfileSidebar
                show={showSidebar}
                onDiscard={handleSidebarClose}
                item={selectedRows[0] || null}
                plantId={context.lastUsedPlant.id}
                onSave={handleSaveDeviceProfile}
                onDelete={handleDeleteDeviceProfile}
            />

            {/* Wizard Modal */}
            {showWizard && (
                <AddDeviceProfileWizard
                    modal={true}
                    profile={selectedRows[0] || null}
                    initialConfig={selectedRows[0]?.configuration}
                    onClose={() => {
                        setShowWizard(false);
                    }}
                    onFinish={async (manifest) => {
                        // Update the device profile configuration
                        if (selectedRows[0]?.id) {
                            await handleUpdateConfiguration(
                                selectedRows[0].id,
                                manifest,
                            );
                        }
                        setShowWizard(false);
                    }}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { DeviceProfiles };
